import clsx from 'clsx';
import { ReactNode, useMemo } from 'react';
import { PosLink } from 'src/core/POS/PosLink/PosLink';
import { Stack } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ParkingPassIcon } from 'src/svgs/Viagogo';
import { EventPageLocationState } from 'src/utils/types/EventPageLocationState';
import {
  ApiEventStatus,
  Feature,
  Tag,
  TopLevelCategory,
} from 'src/WebApiController';

import { EventStatus } from '../EventStatus';
import { EventDeletedIcon } from '../EventStatus/EventDeletedIcon';
import { EventDateDisplay } from './EventDateDisplay';
import * as styles from './EventInfo.css';
import {
  EventMappingStateDisplay,
  EventMappingStateDisplayProps,
} from './EventMappingStateDisplay';
import { EventNameDisplay } from './EventNameDisplay';
import { EventTagsDisplay } from './EventTagsDisplay';
import { VenueNameDisplay } from './VenueNameDisplay';

const DateRelatedEventStatus: ApiEventStatus[] = [
  ApiEventStatus.Rescheduled,
  ApiEventStatus.Postponed,
  ApiEventStatus.RelocatedAndRescheduled,
];
const VenueRelatedEventStatus: ApiEventStatus[] = [
  ApiEventStatus.Relocated,
  ApiEventStatus.Contingent,
  ApiEventStatus.Cancelled,
  ApiEventStatus.RelocatedAndRescheduled,
];

export type EventInfoProps = {
  eventName?: ReactNode;
  /**
   * Portion of the event name to be displayed with less emphasis.
   */
  eventNameConnector?: ReactNode;
  eventSubName?: ReactNode;
  eventDateString?: string;
  eventClassName?: string;
  venueClassName?: string;
  eventDateClassName?: string;
  /**
   * Optional end date that is used to calculate and show the number of dates after the date.
   *
   * eg. Feb 12 - 2:30pm, Sunday (3 days)
   */
  eventEndDateString?: string;
  /**
   * If `eventDate` is `undefined`, shows a `ToBeDeterminedIcon` instead of the event date if this
   * is set to `true`. Otherwise if `false`, event date will be fully omitted.
   */
  showEventDateTbd?: boolean;
  /**
   * Whether to show the year in the event date
   */
  showYear?: boolean;
  venueName?: ReactNode;
  /**
   * The location of the venue (eg. City, State).
   */
  venueLocation?: ReactNode;
  /**
   * Whether to show the mapping state (pending, rejected) of the event.
   */
  showMappingState?: boolean;
  /* Show only event name
   */
  eventNameOnly?: boolean;
  /* Show venue and date in smaller/compressed style */
  slimMode?: boolean;
  slimContent?: boolean;
  truncateTitle?: boolean;
  showParkingPassIcon?: boolean;

  eventGenre?: TopLevelCategory;
  eventNameLink?: string;

  showVenueLocation?: boolean;
  showEventDate?: boolean;
  linkEvent?: boolean;
  hideDetailsIcon?: boolean;

  showEventStatus?: boolean;
  eventStatus?: ApiEventStatus | null;
  dateTimeSeparator?: '@' | '-';
  withEventStatus?: boolean;
  eventTags?: Tag[] | null;
} & EventMappingStateDisplayProps;

/**
 * Component that shows event info.
 * @param props
 * @returns
 */
export function EventInfo({
  eventName,
  eventNameConnector,
  eventSubName,
  eventDateString,
  eventEndDateString,
  showEventDateTbd,
  showMappingState,
  mappingRejectionReasonId,
  mappingRejectionReasonDescriptionDefault,
  eventMappingState,
  eventTags,
  showYear = false,
  venueName,
  venueLocation,
  eventNameOnly,
  slimMode,
  slimContent,
  truncateTitle,
  showParkingPassIcon = false,
  eventGenre,
  eventNameLink,
  eventClassName,
  venueClassName,
  eventDateClassName,
  showVenueLocation = true,
  showEventDate = true,
  linkEvent = true,
  hideDetailsIcon,
  showEventStatus = false,
  eventStatus,
  dateTimeSeparator,
  withEventStatus = false,
}: EventInfoProps) {
  const eventDisplayName = (
    <EventNameDisplay
      eventName={eventName}
      eventNameConnector={eventNameConnector}
      eventSubName={eventSubName}
      slimMode={slimMode}
      slimContent={slimContent}
      boldSubName={eventGenre === TopLevelCategory.Sports}
      className={eventClassName}
    />
  );

  const hasDeletedEventStateDisplayFeature = useUserHasFeature(
    Feature.DeletedEventStateDisplay
  );

  const hasShowEventTagOnEventTileFeature = useUserHasFeature(
    Feature.ShowEventTagOnEventTile
  );

  const [isVenueRelatedStatus, isDateRelatedStatus] = useMemo(() => {
    if (!eventStatus || !withEventStatus) {
      return [false, false];
    }
    return [
      VenueRelatedEventStatus.includes(eventStatus),
      DateRelatedEventStatus.includes(eventStatus),
    ];
  }, [eventStatus, withEventStatus]);

  return (
    <Stack
      className={clsx(styles.root, {
        [styles.rootSlim]: slimMode || slimContent,
      })}
      direction="column"
      gap="s"
      width="full"
    >
      <Stack
        direction="row"
        gap="m"
        justifyContent="spaceBetween"
        className={clsx({ [styles.eventNameLink]: truncateTitle })}
      >
        {linkEvent && eventNameLink ? (
          <PosLink
            className={clsx({ [styles.eventNameLink]: truncateTitle })}
            to={eventNameLink}
            state={
              {
                returnUrl: `${window.location.pathname}${window.location.search}`,
                keepReturnUrlUnchanged: true,
              } as EventPageLocationState
            }
          >
            {eventDisplayName}
          </PosLink>
        ) : (
          eventDisplayName
        )}
        {showParkingPassIcon && (
          <ParkingPassIcon className={styles.eventDisplayIcon} />
        )}
        {hasDeletedEventStateDisplayFeature && showEventStatus && (
          <>
            {!slimMode && eventStatus && <EventStatus status={eventStatus} />}
            {slimMode && eventStatus === ApiEventStatus.Deleted && (
              <EventDeletedIcon />
            )}
          </>
        )}
      </Stack>
      {!eventNameOnly &&
        (venueName ||
          venueLocation ||
          eventDateString ||
          eventEndDateString) && (
          <Stack
            className={styles.eventDetails}
            gap="l"
            direction={slimMode ? 'column' : undefined}
            flexWrap={slimMode ? undefined : 'wrap'}
            alignItems={slimMode ? 'start' : 'center'}
          >
            {withEventStatus ? (
              <>
                <Stack gap="s">
                  {isVenueRelatedStatus && (
                    <EventStatus
                      status={
                        eventStatus === ApiEventStatus.RelocatedAndRescheduled
                          ? ApiEventStatus.Relocated
                          : eventStatus
                      }
                    />
                  )}
                  <VenueNameDisplay
                    venueName={venueName}
                    venueLocation={
                      showVenueLocation ? venueLocation : undefined
                    }
                    slimMode={slimMode}
                    slimContent={slimContent}
                    displayClassName={venueClassName}
                    hideIcon={hideDetailsIcon}
                  />
                </Stack>
                <Stack gap="s">
                  {isDateRelatedStatus && (
                    <EventStatus
                      status={
                        eventStatus === ApiEventStatus.RelocatedAndRescheduled
                          ? ApiEventStatus.Rescheduled
                          : eventStatus
                      }
                    />
                  )}
                  {showEventDate && (
                    <EventDateDisplay
                      eventDateString={eventDateString}
                      eventEndDateString={eventEndDateString}
                      eventDateFormat={showYear ? 'MMM d, y' : undefined}
                      showEventDateTbd={showEventDateTbd}
                      slimMode={slimMode}
                      slimContent={slimContent}
                      displayClassName={eventDateClassName}
                      hideIcon={hideDetailsIcon}
                      dateTimeSeparator={dateTimeSeparator}
                    />
                  )}
                </Stack>
              </>
            ) : (
              <>
                <VenueNameDisplay
                  venueName={venueName}
                  venueLocation={showVenueLocation ? venueLocation : undefined}
                  slimMode={slimMode}
                  slimContent={slimContent}
                  displayClassName={venueClassName}
                  hideIcon={hideDetailsIcon}
                />
                {showEventDate && (
                  <EventDateDisplay
                    eventDateString={eventDateString}
                    eventEndDateString={eventEndDateString}
                    eventDateFormat={showYear ? 'MMM d, y' : undefined}
                    showEventDateTbd={showEventDateTbd}
                    slimMode={slimMode}
                    slimContent={slimContent}
                    displayClassName={eventDateClassName}
                    hideIcon={hideDetailsIcon}
                    dateTimeSeparator={dateTimeSeparator}
                  />
                )}
              </>
            )}
          </Stack>
        )}
      {showMappingState && (
        <EventMappingStateDisplay
          eventMappingState={eventMappingState}
          mappingRejectionReasonId={mappingRejectionReasonId}
          mappingRejectionReasonDescriptionDefault={
            mappingRejectionReasonDescriptionDefault
          }
        />
      )}
      {hasShowEventTagOnEventTileFeature && (
        <EventTagsDisplay tags={eventTags} />
      )}
    </Stack>
  );
}
